import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { NavigationEnd, Router } from '@angular/router';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { DataEntity, MapService } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  BasysMapFrameService,
  PanelComponent,
  RoutingService,
  ViewStateChangeArgType,
} from '@barthauer/basys/map-frame';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { MediaChange, MediaObserver } from 'ngx-flexible-layout';
import { DataSourceFilter, FilterType } from '../../models/componentDataSource.model';
import { AppService } from '../../services/app.service';
import { DataService } from '../../services/data.service';
import { FrameService } from '../../services/frame.service';
import { FilterButton } from '../../shared/helpers';
import { IMobileTicket, MobileTicketViewModel } from '../../viewModels/mobileData.tickets.viewModel';
import { TicketNavigationContext, TicketsDataContext, TicketState, TicketStateChangeArgType } from '../tickets.model';
import { TicketsService } from '../tickets.service';

@Component({
  selector: 'app-ticket-list',
  templateUrl: './ticket-list.component.html',
  styleUrls: ['./ticket-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TicketListComponent extends PanelComponent implements OnInit, OnDestroy {
  @ViewChild('ticketListScrollViewPort', { static: true }) ticketListScrollViewPort: CdkVirtualScrollViewport;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  readonly VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT: number = 87; //header-bar + sort bar height
  readonly VIEWPORTHEIGHTVERTICALMARGIN_XS_WITH_FILTERCHIPS: number = 118; //header-bar + sort bar + "chips bar" height = 117 on desktop, 118 on mobile is better :-/
  private hasReminders: boolean = false;
  private detailTicketOffset: number;
  private currentViewPortSize: string;
  private ticketStateFilters: Map<any, DataSourceFilter>;
  public res = ResourceSet.Empty;
  AppService = AppService;
  scrollViewPortElementName: string = 'ticketListScrollViewPort';
  public dataContext: TicketsDataContext;
  public selectedTicketStates: string[] = [
    TicketState.Offen,
    TicketState.InBearbeitung,
    TicketState.Erledigt,
    TicketState.Push,
  ];
  public ticketStateButtons: FilterButton[] = [];

  public get scrollViewPortHeightVerticalMargin(): number {
    if (this.dataContext && this.dataContext.dataSource.filters.length > 0) {
      return this.currentViewPortSize === 'xs'
        ? this.VIEWPORTHEIGHTVERTICALMARGIN_XS_WITH_FILTERCHIPS
        : this.VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT;
    } else {
      return this.VIEWPORTHEIGHTVERTICALMARGIN_DEFAULT;
    }
  }

  constructor(
    private ticketsService: TicketsService,
    private frameService: FrameService,
    private resourceService: ResourceManagerService,
    frame: BasysMapFrameService,
    mediaObserver: MediaObserver,
    router: Router,
    private mapService: MapService,
    private routingService: RoutingService,
    private dataService: DataService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
    super.init(frame);
    this.ticketStateFilters = new Map<any, DataSourceFilter>();

    this.subscriptions.push(
      frame.lib.onViewStateChanged().subscribe((args) => {
        switch (args.type) {
          case ViewStateChangeArgType.BottomSheetPanelOpened: {
            this.ticketListScrollViewPort.checkViewportSize();
            break;
          }
        }
        //needed?!
        // const bottomSheetElement = document.querySelector(
        //   '.bottom-sheet-container > .mat-bottom-sheet-container'
        // ) as HTMLElement;
        // if (bottomSheetElement) {
        //   const startPos = bottomSheetElement.offsetTop;
        //   const newTop = startPos;
        //   const screenHeight = window.innerHeight;
        //   const ticketListElement = document.querySelector('.ticketListScrollViewPort') as HTMLElement;
        //   if (ticketListElement) {
        //     ticketListElement.style.setProperty('height', screenHeight - newTop - 151 + 'px', 'important');
        //   }
        // }
      }),
    );

    this.subscriptions.push(
      ticketsService.onTicketStateChanged().subscribe((args) => {
        switch (args.type) {
          case TicketStateChangeArgType.TicketsFilterApplied: {
            this.ensureViewPortSize();
            break;
          }
        }
      }),
    );

    this.subscriptions.push(
      ticketsService.onDataContextSent().subscribe(async (context) => {
        this.dataContext = context;

        this.dataContext.dataSource.sort = this.sort;
        this.sortData();
        this.cdr.detectChanges();

        this.ensureTicketStateFilter(TicketState.Offen);
        this.ensureTicketStateFilter(TicketState.InBearbeitung);
        this.ensureTicketStateFilter(TicketState.Erledigt);
        this.ensureTicketStateFilter(TicketState.Push);

        this.selectedTicketStates = this.dataContext.selectedTicketStates;

        this.hasReminders = await this.dataService.hasMtTicketReminders();

        if (this.hasReminders) {
          const activeRemindersCount = this.countActiveReminders();

          const pushBadge = this.ticketStateButtons.find((button) => button.id === TicketState.Push);

          if (pushBadge) {
            pushBadge.badge = activeRemindersCount;
          }
          this.cdr.detectChanges();
        }
      }),
    );

    this.subscriptions.push(
      router.events.subscribe((e) => {
        if (
          e instanceof NavigationEnd &&
          routingService.getCurrentConfig().designation === AppService.Route_TicketList
        ) {
          this.ticketListScrollViewPort.scrollToOffset(this.detailTicketOffset, 'auto');
          this.cdr.detectChanges();
        }
      }),
    );

    this.subscriptions.push(
      // BUG
      mediaObserver.asObservable().subscribe((changes: MediaChange[]) => {
        this.currentViewPortSize = changes[0].mqAlias;
        this.ensureViewPortSize();
      }),
    );
  }

  async ngOnInit() {
    super.ngOnInit();
    this.res = await this.resourceService.getResourceSet('abc-maintenance-opera');

    this.subscriptions.push(
      this.frameService.mobileTicketsSubject.subscribe((ticketPackage) => {
        this.dataContext.dataSource.rawData = ticketPackage.allTickets;
        this.refreshTicketList();
      }),
    );

    this.ticketStateButtons = [
      {
        id: TicketState.Offen,
        icon: this.res.svgUrl('StateOpen-Image'),
        hint: 'Nicht begonnen',
        predicate: (obj) => obj.hasState(TicketState.Offen),
        badge: null,
      },
      {
        id: TicketState.InBearbeitung,
        icon: this.res.svgUrl('StatePartial-Image'),
        hint: 'Teilerledigt',
        predicate: (obj) => obj.hasState(TicketState.InBearbeitung),
        badge: null,
      },
      {
        id: TicketState.Erledigt,
        icon: this.res.svgUrl('StateDone-Image'),
        hint: 'Erledigt',
        predicate: (obj) => obj.hasState(TicketState.Erledigt),
        badge: null,
      },
    ];
    this.hasReminders = await this.dataService.hasMtTicketReminders();

    if (this.hasReminders) {
      this.ticketStateButtons.unshift({
        id: TicketState.Push,
        icon: this.res.svgUrl('StatePush-Image'),
        hint: 'Tickets with active reminders',
        predicate: (obj: MobileTicketViewModel) => obj.hasActiveReminders,
        badge: null,
      });
    }

    if (this.dataContext) {
      // Only after all buttons are initialized, ensure filters are in place
      this.ensureTicketStateFilter(TicketState.Offen);
      this.ensureTicketStateFilter(TicketState.InBearbeitung);
      this.ensureTicketStateFilter(TicketState.Erledigt);

      if (this.hasReminders) {
        this.ensureTicketStateFilter(TicketState.Push);
      }
    }
    this.cdr.detectChanges();
  }

  private ensureViewPortSize(): void {
    super.updateSize();
    this.cdr.detectChanges();
  }

  private refreshTicketList(): void {
    if (this.ticketListScrollViewPort) {
      this.ticketListScrollViewPort.detach();

      setTimeout(() => {
        this.ticketListScrollViewPort.checkViewportSize();
        this.cdr.detectChanges();
      }, 0);
    }
  }

  private ensureTicketStateFilter(ticketStateString: string): void {
    const button = this.ticketStateButtons.find((x) => x.id === ticketStateString);

    if (!button) {
      console.warn(`Warning: Ticket state button for ${ticketStateString} is undefined.`);
      return;
    }

    if (!this.ticketStateFilters.has(ticketStateString)) {
      const ds = this.createAndApplyTicketStateFilter(ticketStateString, button.predicate);
      this.ticketStateFilters.set(ticketStateString, ds);
    }
  }

  private createAndApplyTicketStateFilter(
    ticketStateString: string,
    predicate: (obj: IMobileTicket) => boolean,
  ): DataSourceFilter {
    const f: DataSourceFilter = new DataSourceFilter();
    f.type = FilterType.Predicate;
    f.predicate = predicate;
    f.displayText = ticketStateString;
    f.visible = false;
    this.dataContext.applyFilterObject(f);
    return f;
  }

  public removeFilter(filter: DataSourceFilter): void {
    if (filter.type === FilterType.Action) {
      filter.action();
    }
    this.dataContext.dataSource.removeFilter(filter);
    this.ensureViewPortSize();
    this.ticketListScrollViewPort.checkViewportSize();
    this.sortData();
  }

  public navigateToTicketDetail(ticketIndex: number): void {
    this.detailTicketOffset = this.ticketListScrollViewPort.measureScrollOffset();
    this.routingService.navigateName(AppService.Route_TicketDetail).then((succ) => {
      if (succ) {
        const context: TicketNavigationContext = new TicketNavigationContext();
        context.ticketDataContext = this.dataContext;
        context.ticketIndex = ticketIndex;
        this.ticketsService.refreshNavigationContext(context);
      }
    });
  }

  public ticketStateFilterClick(e): void {
    this.dataContext.selectedTicketStates = this.selectedTicketStates;
    const toogled: boolean = this.dataContext.selectedTicketStates.some((x) => x === e.itemData.id);

    const filter = this.ticketStateFilters.get(e.itemData.id);

    if (filter) {
      filter.enabled = toogled;
    }
    this.dataContext.updateFilter();
    this.sortData();
  }
  private countActiveReminders(): number {
    if (!this.dataContext || !this.dataContext.dataSource || !this.dataContext.dataSource.internalDataSource) {
      return 0;
    }

    const tickets = this.dataContext.dataSource.internalDataSource.data as MobileTicketViewModel[];
    const count = tickets.filter((ticket) => ticket.hasActiveReminders).length;
    return count;
  }

  public selectTicketInMap(ticket: MobileTicketViewModel): void {
    const targetEntities: DataEntity[] = [];
    if (ticket.hasGeometry) {
      const ticketEntity = new DataEntity(ticket.objectType, ticket.id);
      targetEntities.push(ticketEntity);
    }

    if (!ticket.hasGeometry && ticket.object && ticket.object.drawableObjectId && ticket.object.drawableObjectType) {
      const objEntity: DataEntity = new DataEntity(ticket.object.drawableObjectType, ticket.object.drawableObjectId);
      targetEntities.push(objEntity);
    }

    this.mapService.navigateToObjects(targetEntities);
    this.frame.lib.closeBottomSheet();
  }

  public trackById(index, item) {
    return item.id;
  }

  public sortData(): void {
    this.dataContext.dataSource.internalDataSource.sortData(
      this.dataContext.dataSource.internalDataSource.filteredData,
      this.dataContext.dataSource.sort,
    );
  }

  public hasReminderFilter(): boolean {
    return this.ticketStateButtons.some((button) => button.id === TicketState.Push);
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
